import React, { useState } from 'react';
import { Button, Timeline, Typography } from 'antd';

import {
  AbtBotOption, SenderEvent, formatDateTime, formatSender, plural, statusLabel,
} from './shared';
import { countOf, kindLabel } from './templateMapping';

const { Text } = Typography;

const COLLAPSED = 10;

const actorOf = (e: SenderEvent): string => e.actorName || 'GoHire';

/** One event → [headline, includesActor]. Headlines that already name the actor skip the "by …" suffix. */
export function describeEvent(e: SenderEvent, botNames: Record<string, string> = {}): [string, boolean] {
  const d = e.details || {};
  switch (e.action) {
    case 'registered':
      return [`Registered${d.sender ? ` ${formatSender(d.sender)}` : ''} by ${actorOf(e)}`, true];
    case 'updated': {
      const parts: string[] = [];
      if (d.displayName !== undefined) parts.push(`Display name changed to “${d.displayName}”`);
      if (d.status !== undefined) parts.push(`Status set to ${statusLabel(d.status)}`);
      if (d.abtBotId !== undefined) {
        if (!d.abtBotId) parts.push('Apply-By-Text bot removed (inbox only)');
        else parts.push(botNames[d.abtBotId] ? `Apply-By-Text bot set to ${botNames[d.abtBotId]}` : 'Apply-By-Text bot changed');
      }
      if (d.wabaId !== undefined) parts.push(d.wabaId ? `WABA ID set to ${d.wabaId}` : 'WABA ID cleared');
      return [parts.length ? parts.join(' · ') : 'Number updated', false];
    }
    case 'removed': {
      const held = Number(d.heldCancelled || 0);
      const scheduled = Number(d.scheduledCancelled || 0);
      const tail = held || scheduled ? ` (${held} held, ${scheduled} scheduled cancelled)` : '';
      return [`Removed by ${actorOf(e)}${tail}`, true];
    }
    case 'moved_out':
      return [`Moved to ${d.toCompanyName || 'another company'}`, false];
    case 'moved_in':
      return [`Moved in from ${d.fromCompanyName || 'another company'}`, false];
    case 'user_added':
      return [`${d.userName || 'A user'} put back on`, false];
    case 'user_removed': {
      const released = Number(d.conversationsReleased || 0);
      return [`${d.userName || 'A user'} taken off${released ? ` (${released} returned to the queue)` : ''}`, false];
    }
    case 'templates_synced': {
      if (d.ok === false) return [`Template sync from Infobip failed${d.error ? `: ${d.error}` : ''}`, false];
      const counts = [
        countOf(d.added) ? `${countOf(d.added)} added` : '',
        countOf(d.removed) ? `${countOf(d.removed)} removed` : '',
        countOf(d.cleared) ? `${countOf(d.cleared)} need re-mapping` : '',
      ].filter(Boolean);
      const totalRaw = d.total !== undefined ? d.total : d.synced;
      const total = totalRaw !== undefined ? ` ${plural(countOf(totalRaw), 'template')}` : ' templates';
      return [`Synced${total} from Infobip${counts.length ? ` (${counts.join(', ')})` : ''}`, false];
    }
    case 'template_mapped': {
      const name = d.templateName || d.name || 'a template';
      const use = d.kind !== undefined ? ` · ${kindLabel(d.kind)}` : '';
      return [`Mapped ${name}${use}`, false];
    }
    default:
      return [String(e.action || 'Changed'), false];
  }
}

const dotColor = (action: string): string => {
  if (action === 'registered' || action === 'moved_in') return 'green';
  if (action === 'removed' || action === 'moved_out') return 'red';
  if (action === 'user_removed') return 'gray';
  if (action === 'templates_synced' || action === 'template_mapped') return 'cyan';
  return 'blue';
};

interface Props {
  events: SenderEvent[];
  abtBots?: AbtBotOption[];
}

/** Compact action history for the company's WhatsApp number(s), newest first. */
const SenderHistory: React.FC<Props> = ({ events, abtBots = [] }) => {
  const [expanded, setExpanded] = useState(false);
  if (!events || !events.length) return null;

  const botNames = abtBots.reduce<Record<string, string>>((acc, b) => ({ ...acc, [b.value]: b.label }), {});
  // Newest first. A move writes `registered` + `moved_in` in one transaction (same timestamp):
  // on a tie, `registered` sorts as the older event.
  const sorted = [...events].sort((a, b) => (
    String(b.createdAt).localeCompare(String(a.createdAt))
    || Number(a.action === 'registered') - Number(b.action === 'registered')
  ));
  const shown = expanded ? sorted : sorted.slice(0, COLLAPSED);

  return (
    <div data-testid="company-whatsapp-history">
      <Text strong style={{ display: 'block', marginBottom: 12 }}>History</Text>
      <Timeline
        items={shown.map((e) => {
          const [headline, namesActor] = describeEvent(e, botNames);
          const when = formatDateTime(e.createdAt);
          return {
            key: e.id,
            color: dotColor(e.action),
            children: (
              <div data-testid="company-whatsapp-history-item" data-action={e.action}>
                <Text style={{ fontSize: 13 }}>{headline}</Text>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {namesActor ? when : `by ${actorOf(e)} · ${when}`}
                </Text>
              </div>
            ),
          };
        })}
      />
      {sorted.length > COLLAPSED && (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show all (${sorted.length})`}
        </Button>
      )}
    </div>
  );
};

export default SenderHistory;
